import { ConnectAccountOnboarding, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Shadcn Components
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Hooks
import { useStripeConnect } from "../../hooks/common/useStripeConnect";
import { useStripeAccountOnboarding } from "../../hooks/authentication/useStripeAccountOnboarding";
import { useOrganizationCreation } from "../../hooks/authentication/useOrganizationCreation";
import { useOrganizationOnboardingFlag } from "../../hooks/common/useFeatureFlag";

// Icons
import { ArrowLeft, Monitor, Loader2 } from "lucide-react";

export default function AccountOnboarding() {
  const navigate = useNavigate();
  const {
    accountCreatePending,
    onboardingCompleted,
    error: stripeError,
    connectedAccountId,
    handleStartSetup,
    handleQuit,
    handleOnboardingComplete,
  } = useStripeAccountOnboarding();

  const {
    error: orgError,
    createOrganization
  } = useOrganizationCreation(connectedAccountId);

  const stripeConnectInstance = useStripeConnect(connectedAccountId);

  const { isEnabled: organizationOnboardingEnabled, loading } = useOrganizationOnboardingFlag();

  // Redirect to auth choice if organization onboarding is disabled
  useEffect(() => {
    if (!loading && !organizationOnboardingEnabled) {
      navigate('/auth');
    }
  }, [organizationOnboardingEnabled, loading, navigate]);

  // Show loading or redirect if feature is disabled
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!organizationOnboardingEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  const error = stripeError || orgError;

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12" 
      style={gradientStyle}
    >
      <div className="flex flex-col w-[90%] sm:w-[85%] md:w-[75%] lg:w-[65%] xl:w-[55%] max-w-3xl rounded-lg overflow-hidden bg-white shadow-2xl">
        <div className="w-full bg-white p-2 px-6">
          <div className="w-full max-w-[600px] mx-auto flex flex-col h-[calc(100vh-160px)]">
            <div className="flex flex-col items-center relative mb-2">
              <div className="bg-white p-4 pb-0 rounded-lg">
                <img 
                  src="/branding/TriEvents_Loading_Big_Text.gif" 
                  alt="TriEvents Logo" 
                  className="w-40 sm:w-48 h-auto"
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-semibold text-foreground text-center">
                Account Onboarding
              </h1>
              <p className="text-muted-foreground text-center text-sm max-w-[500px] font-medium mt-0.5 mb-4">
                Complete your account setup to start accepting payments
              </p>
            </div>

            <div className="flex-1 overflow-y-auto rounded-lg pt-6 border bg-gray-50 scrollbar-thin scrollbar-track-muted/20 scrollbar-thumb-muted/40 hover:scrollbar-thumb-muted/60">
              <div className="w-full mx-auto px-3">
                {error && (
                  <Alert variant="destructive" className="mb-4 bg-white">
                    <AlertDescription>
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <Card className="mb-6">
                  <CardContent className="pt-6">
                    {!connectedAccountId && (
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <Monitor className="h-5 w-5 flex-shrink-0 text-yellow-600" />
                          <p>For the best experience, we recommend completing this setup on a desktop device.</p>
                        </div>
                      </div>
                    )}

                    {accountCreatePending && (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground">Creating your account...</p>
                      </div>
                    )}

                    {onboardingCompleted && (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground">Setting up your organization...</p>
                      </div>
                    )}

                    {stripeConnectInstance && !accountCreatePending && !onboardingCompleted && (
                      <div className="md:block overflow-x-auto md:overflow-x-visible -mx-3 md:mx-0">
                        <div className="md:min-w-0 min-w-[600px] px-3 md:px-0">
                          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                            <ConnectAccountOnboarding
                              onExit={() => {
                                handleOnboardingComplete();
                                if (connectedAccountId) {
                                  createOrganization();
                                }
                              }}
                            />
                          </ConnectComponentsProvider>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="sticky bottom-0 left-0 right-0 bg-white py-4">
              <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
                <Button
                  variant="outline"
                  onClick={handleQuit}
                  className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Quit
                </Button>
                {!connectedAccountId && !accountCreatePending && !onboardingCompleted && (
                  <Button
                    onClick={handleStartSetup}
                    size="lg"
                    className="w-full sm:w-[280px] transition-transform hover:scale-105"
                  >
                    {accountCreatePending ? 'Creating Account...' : 'Start Setup'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}